import { useState } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/lib/api';

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === 'admin' ? '/dashboard' : '/portal'} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from || (user.role === 'admin' ? '/dashboard' : '/portal');
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-32 md:pt-40 pb-24 container mx-auto">
        <div className="max-w-md mx-auto rounded-3xl bg-card border border-border/70 shadow-card p-8 sm:p-10">
          <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-primary mt-5">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Wholesale customers and VDBF staff. Accounts are invitation-only —
            <Link to="/wholesale#request" className="text-leaf hover:underline underline-offset-4"> request access here</Link>.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="login-email" className="text-primary mb-1.5">Email</Label>
              <Input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="h-11 rounded-xl" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="login-password" className="text-primary">Password</Label>
                <Link to="/forgot-password" className="text-xs text-leaf hover:underline underline-offset-4" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="h-11 rounded-xl" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </main>
  );
}
